import { IPaymentProvider } from "@/types/IPaymentProvider";

export function providerKeyBuilder(providers: IPaymentProvider[]): (provider: IPaymentProvider) => string {
  const names = providers.map((provider) => provider.name);
  const repeatedNames = new Set(names.filter((name, index) => names.indexOf(name) !== index));
  return (provider) =>
    repeatedNames.has(provider.name)
      ? `provider-${provider.name}-${provider.displayName}`
      : `provider-${provider.name}`;
}

export function groupProvidersByFee(providers: IPaymentProvider[]) {
  // sort fee from biggest to smallest
  providers.sort((a, b) => b.fee - a.fee);
  return providers.reduce((groups, provider) => {
    const fee = provider.fee;
    if (!groups[fee]) {
      groups[fee] = [];
    }
    groups[fee].push(provider);
    return groups;
  }, {} as Record<number, IPaymentProvider[]>);
}

const NUMERIC_REFERENCE_PATTERN = /^\d{5,}$/;

/**
 * Formats a payment reference for on-screen display: all-digit references longer
 * than four characters (CLABEs, barcodes, agreement numbers) are shown as three
 * groups of four followed by the remainder, e.g.
 * "710969000452066612" -> "7109 6900 0452 066612". Anything else (alphanumeric
 * codes, URLs, short values) is returned unchanged.
 *
 * Display-only: callers must copy/share/encode the raw (unspaced) value.
 */
export function formatReferenceDisplay(value: string): string {
  const compact = value.replace(/\s+/g, "");
  if (!NUMERIC_REFERENCE_PATTERN.test(compact)) return value;
  const groups = [compact.slice(0, 4), compact.slice(4, 8), compact.slice(8, 12), compact.slice(12)];
  return groups.filter(Boolean).join(" ");
}

const TOKU_BASE_URL = "https://pagosmx.payjoy.com/new-forms/signup";

export function buildTokuUrl(params: {
  customerUserId: number | undefined;
  deviceTag: string;
  financeProductType: string;
  financeProductId: number | null;
}): string | null {
  const { customerUserId, deviceTag, financeProductType, financeProductId } = params;
  if (!customerUserId || !financeProductId) return null;
  const searchParams = new URLSearchParams({
    externalid: String(customerUserId),
    devicetag: deviceTag,
    productId: `${financeProductType}-${financeProductId}`,
  });
  return `${TOKU_BASE_URL}?${searchParams.toString()}`;
}

export interface ExceededProviderLimit {
  /** vue-i18n key for the message. */
  messageKey: string;
  /** Raw numeric limit — caller formats it with `computedAmountValue`. */
  maxAmount: number;
}

/** Returns the resolved limit when `amount` exceeds the provider's
 *  configured max, otherwise null. The cap is sourced from the backend
 *  `PaymentProvider.max_amount` column (exposed as `provider.maxAmount`). */
export function getExceededProviderLimit(provider: IPaymentProvider, amount: number): ExceededProviderLimit | null {
  if (provider.maxAmount == null) return null;
  if (amount <= provider.maxAmount) return null;
  return {
    messageKey: "Provider-limit-exceeded-message",
    maxAmount: provider.maxAmount,
  };
}

export interface BelowMinimumProviderLimit {
  /** vue-i18n key for the message. */
  messageKey: string;
  /** Raw numeric limit — caller formats it with `computedAmountValue`. */
  minAmount: number;
}

/** Returns the resolved floor when `amount` is below the provider's
 *  configured min, otherwise null. The floor is sourced from the backend
 *  `PaymentProvider.min_amount` column (exposed as `provider.minAmount`). */
export function getBelowMinimumProviderLimit(
  provider: IPaymentProvider,
  amount: number,
): BelowMinimumProviderLimit | null {
  if (provider.minAmount == null) return null;
  if (amount >= provider.minAmount) return null;
  return {
    messageKey: "Provider-limit-below-minimum-message",
    minAmount: provider.minAmount,
  };
}
